/** @typedef {import("@/services/ContextDetectionService").MenuState} MenuState */
/** @typedef {import("@/services/ContextDetectionService").MenuContext} MenuContext */

/**
 * Base configuration class for right-click context menus
 * Provides common patterns and helper methods for defining menu structures
 */
class BaseRightClickMenuConfig {
  constructor() {
    this.menuItems = [];
    this.conditions = {};
  }

  /**
   * Add a menu item to the configuration
   * @param {Object} item - Menu item definition
   * @param {string} item.action - Action identifier
   * @param {string|((context: MenuState) => string)} item.icon - Icon name or function that returns icon name
   * @param {string|((context: MenuState, additionalContext?: Object) => string)} item.label - Label text or function that returns label
   * @param {string} [item.hotkey] - Keyboard shortcut hint text
   * @param {(context: MenuState, additionalContext?: Object) => boolean} [item.conditions] - Function to determine if item should show
   * @param {(context: MenuState) => boolean} [item.disabled] - Function to determine if item should be disabled
   * @param {number} [item.order=100] - Sort order for the menu item
   * @param {string} [item.className] - CSS class name for the menu item
   * @returns {BaseRightClickMenuConfig} This instance for method chaining
   */
  addMenuItem(item) {
    const menuItem = {
      action: item.action,
      icon: item.icon,
      label: item.label,
      conditions: item.conditions || (() => true),
      order: item.order ?? 100,
      ...item,
    };
    this.menuItems.push(menuItem);
    return this;
  }

  addDivider(order = 100) {
    this.menuItems.push({
      type: "divider",
      order,
    });
    return this;
  }

  addSubmenu(submenu) {
    const submenuItem = {
      type: "submenu",
      label: submenu.label,
      disabled: submenu.disabled || (() => false),
      icon: submenu.icon,
      items: submenu.items || [],
      conditions: submenu.conditions || (() => true),
      order: submenu.order || 100,
      className: submenu.className,
    };
    this.menuItems.push(submenuItem);
    return this;
  }

  /**
   * Get filtered and sorted menu items based on context
   * @param {MenuState} context - Context information from right-click detection
   * @param {Object} additionalContext - Additional context like clipboard state
   * @returns {Array} Filtered and sorted menu items with orphaned dividers removed
   */
  getMenuItems(context, additionalContext = {}) {
    const filteredItems = this.menuItems
      .filter((item) => {
        if (item.conditions && typeof item.conditions === "function") {
          return item.conditions(context, additionalContext);
        }
        return true;
      })
      .sort((a, b) => (a.order || 100) - (b.order || 100));

    return this.removeOrphanedDividers(filteredItems);
  }

  removeOrphanedDividers(items) {
    const result = items.reduce((acc, item, i) => {
      if (item.type === "divider") {
        const hasPreviousItem = acc.some((prevItem) => prevItem.type !== "divider");
        const hasNextItem = items.slice(i + 1).some((nextItem) => nextItem.type !== "divider");
        if (hasPreviousItem && hasNextItem) {
          acc.push(item);
        }
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    return result;
  }

  /**
   * Helper to check if entity is a block
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isBlock(context) {
    return context.entityType === "block";
  }

  /**
   * Helper to check if entity is an element
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isElement(context) {
    return context.entityType === "element";
  }

  /**
   * Helper to check if selection is a single element
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isSingleElement(context) {
    return context.context?.isSingleElement;
  }

  /**
   * Helper to check if selection is multiple elements
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isMultipleElements(context) {
    return !BaseRightClickMenuConfig.isBlock(context) && !context.context?.isSingleElement;
  }

  /**
   * Helper to check if dealing with an unselected block
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isUnselectedBlock(context) {
    return context.context?.isUnselectedBlock;
  }

  /**
   * Helper to check if element is locked
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isLocked(context) {
    return context.context?.isLocked;
  }

  /**
   * Helper to check if it's a site block instance
   * @param {MenuState} context
   * @returns {boolean}
   */
  static isSiteBlockInstance(context) {
    return context.context?.isSiteBlockInstance;
  }
}

export default BaseRightClickMenuConfig;
